import { Component, Input, OnChanges } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Destructible } from '../destructible';

/**
 * RP-4071: the one control that silences automated notifications to a single contact
 * record. One component rather than a checkbox per surface, because the label is a promise
 * about WHICH channels go quiet — SMS and email; in-app alerts keep firing — and that
 * promise has to read identically on charity, store and partner contacts.
 *
 * The control it binds is a plain boolean; translating it into the API's per-channel
 * `skipSms` / `skipEmail` pair is `contactSilencePatch` in @domains, at submit time.
 */
@Component({
  selector: 'rspl-contact-silence-toggle',
  templateUrl: './contact-silence-toggle.component.html',
  imports: [MatCheckbox, ReactiveFormsModule],
})
export class ContactSilenceToggleComponent extends Destructible implements OnChanges {
  @Input() control: FormControl<boolean>;
  /**
   * The contact group this toggle belongs to. There is nothing to silence until the
   * contact holds a name, an email or a phone — and the submit path deliberately drops the
   * flags for an empty contact (writing them would save a blank contact over the stored
   * one), so without this the checkbox would tick, save without error and come back
   * unticked. Same emptiness test as `contactWithSilence`.
   */
  @Input() contact?: FormGroup;

  private contactChanges?: Subscription;

  // ngOnChanges, not ngOnInit: the charity and partner tabs rebuild their whole form in
  // their own ngOnChanges (initContactsForm), so both inputs are replaced with new
  // instances after every save — a subscription taken once would be left watching a
  // discarded FormGroup and the checkbox would stop enabling/disabling.
  ngOnChanges(): void {
    this.contactChanges?.unsubscribe();
    if (!this.contact) return;
    this.syncEnabled();
    this.contactChanges = this.contact.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.syncEnabled());
  }

  private syncEnabled(): void {
    const { name, email, phone } = this.contact?.value ?? {};
    const hasContact = !!(name?.trim() || email?.trim() || phone?.trim());
    if (hasContact === this.control.enabled) return;
    if (hasContact) {
      this.control.enable({ emitEvent: false });
    } else {
      this.control.disable({ emitEvent: false });
    }
  }
}
